import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Single, shared error-message extractor. Never returns "[object Object]". ----
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : (typeof e === "string" ? e : "")))
      .filter(Boolean).join(" ") || "Something went wrong. Please try again.";
  if (typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.detail === "string") return detail.detail;
  }
  return "Something went wrong. Please try again.";
}

// Extract a readable message from a full axios error (handles network / no response too).
export function getErrorMessage(error) {
  if (!error) return "Something went wrong. Please try again.";
  const data = error.response?.data;
  if (data != null) {
    if (data.detail != null) return formatApiError(data.detail);
    if (typeof data.message === "string") return data.message;
    if (typeof data === "string") return data;
  }
  if (error.code === "ERR_NETWORK" || error.message === "Network Error")
    return "Network error. Please check your connection and try again.";
  if (typeof error.message === "string" && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

// True when an error is the backend "subscription required" (402) signal.
export function isSubscriptionError(error) {
  const st = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const code = detail && typeof detail === "object" ? detail.code : null;
  return st === 402 || code === "subscription_required";
}

// PlanContext registers a handler so ANY 402 anywhere opens the Upgrade sheet.
let _subscriptionRequiredHandler = null;
export function setSubscriptionRequiredHandler(fn) { _subscriptionRequiredHandler = fn; }

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (isSubscriptionError(error)) {
      error.isSubscriptionError = true;
      const detail = error?.response?.data?.detail;
      if (_subscriptionRequiredHandler) {
        try { _subscriptionRequiredHandler(detail && typeof detail === "object" ? detail : null); } catch { /* noop */ }
      }
      return Promise.reject(error);
    }
    // Expired/invalid session on an authenticated request -> send to login (guests & auth
    // calls are left alone so public browsing and wrong-password errors keep working).
    const st = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthCall = url.includes("/auth/");
    const hasToken = !!localStorage.getItem("pm_token");
    if (st === 401 && hasToken && !isAuthCall) {
      localStorage.removeItem("pm_token");
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/register")) {
        try { window.sessionStorage.setItem("pm_login_notice", "Please log in again."); } catch { /* noop */ }
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

export default api;
